import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, MessageCircle, Mail, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PurchaseConfirmed = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 sm:px-6 py-12">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-black/5 rounded-full blur-3xl scale-150 animate-pulse" />
            <CheckCircle 
              className="w-20 h-20 sm:w-24 sm:h-24 text-black relative" 
              strokeWidth={1.5}
            />
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">
            Payment Successful!
          </h1>
          <p className="text-base sm:text-lg text-[#666666] font-light max-w-md mx-auto">
            Thank you for choosing Cortexa. Your order has been confirmed.
          </p>
        </div>

        {/* Divider */}
        <div className="w-16 h-px bg-black/20 mx-auto mb-12" />

        {/* Next Steps */}
        <div className="bg-[#fafafa] border border-black/10 p-6 sm:p-8 mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-black mb-4">
            What's Next?
          </h2>
          <p className="text-sm sm:text-base text-[#666666] mb-6 font-light leading-relaxed">
            Our team will contact you shortly to discuss your project details and begin the creation process. 
            In the meantime, feel free to reach out to us directly.
          </p>

          {/* Contact Methods */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" strokeWidth={2} />
              Contact Us Directly
            </h3>

            {/* WhatsApp */}
            <a
              href="https://wa.me/919876543210?text=Hi%20Cortexa,%20I%20just%20completed%20my%20payment"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 border border-black/10 hover:border-black transition-all duration-300 group bg-white"
            >
              <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-grow">
                <div className="text-sm font-bold text-black">WhatsApp</div>
                <div className="text-xs sm:text-sm text-[#666666] font-light">
                  +91 98765 43210
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-black/50 group-hover:text-black group-hover:translate-x-1 transition-all" />
            </a>

            {/* Email */}
            <a
              href="mailto:hello@cortexahq.com?subject=Payment%20Confirmation"
              className="flex items-center gap-4 p-4 border border-black/10 hover:border-black transition-all duration-300 group bg-white"
            >
              <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-grow">
                <div className="text-sm font-bold text-black">Email</div>
                <div className="text-xs sm:text-sm text-[#666666] font-light">
                  hello@cortexahq.com
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-black/50 group-hover:text-black group-hover:translate-x-1 transition-all" />
            </a>

            {/* Phone */}
            <a
              href="tel:+919876543210"
              className="flex items-center gap-4 p-4 border border-black/10 hover:border-black transition-all duration-300 group bg-white"
            >
              <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-grow">
                <div className="text-sm font-bold text-black">Phone</div>
                <div className="text-xs sm:text-sm text-[#666666] font-light">
                  +91 98765 43210
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-black/50 group-hover:text-black group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/home')}
            className="flex-1 bg-black text-white hover:bg-black/90 py-6 text-sm sm:text-base font-medium"
          >
            Back to Home
          </Button>
          <Button
            onClick={() => navigate('/portfolio')}
            variant="outline"
            className="flex-1 border-2 border-black text-black hover:bg-black hover:text-white py-6 text-sm sm:text-base font-medium"
          >
            View Our Work
          </Button>
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs sm:text-sm text-[#666666] mt-8 font-light">
          You will receive an email confirmation shortly with your order details.
        </p>
      </div>
    </div>
  );
};

export default PurchaseConfirmed;
